// Navigation-mode badge. A small square icon button at the bottom, to the right
// of the gnomon, showing the active scene-navigation mode (orbit / turntable /
// fly) as a FontAwesome glyph. Click cycles; N does too. Subscribes to
// params.navMode so it stays in sync with palette + hotkey.
//
// Standalone (own DOM element, own disposer) so it survives the gnomon's
// dispose/reinstall during a projection swap.

import { store } from '@store/instance';
import { INTENTS } from '@store/intents';
import type { NavMode } from '@store/schema';

const LABEL: Record<NavMode, string> = {
    orbit: 'Orbit',
    turntable: 'Turntable',
    fly: 'Fly',
};

// FontAwesome 5 solid glyphs (the loaded set — see index.html).
const ICON: Record<NavMode, string> = {
    orbit: 'fas fa-sync-alt',
    turntable: 'fas fa-compact-disc',
    fly: 'fas fa-plane',
};

const HINT: Record<NavMode, string> = {
    orbit: 'Orbit — drag to orbit, W/S dolly, A/D azimuth, Q/E elevation, wheel zoom (to cursor). Click or press N to toggle fly.',
    turntable: 'Turntable — slow auto-spin; drag to reposition (spin resumes), W/S dolly. Click or press N returns to orbit. (Enable via ⌘K or console.)',
    fly: 'Fly (6DOF) — click or drag to look, WASD move, Q/E down/up, Shift boost. N or Esc to exit.',
};

export function installNavModeBadge(): () => void {
    const badge = document.createElement('button');
    badge.id = 'nav-mode-badge';
    badge.className = 'nav-mode-badge';
    badge.type = 'button';
    const icon = document.createElement('i');
    icon.setAttribute('aria-hidden', 'true');
    badge.appendChild(icon);
    (document.getElementById('bottom-cluster') ?? document.body).appendChild(badge);

    const refresh = (): void => {
        const mode = (store.get('params.navMode') as NavMode) ?? 'orbit';
        icon.className = ICON[mode];
        badge.dataset.mode = mode;
        badge.title = HINT[mode];
        badge.setAttribute('aria-label', `Scene navigation: ${LABEL[mode]} (click to cycle)`);
    };

    const onClick = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        store.dispatch(INTENTS.CAMERA_CYCLE_NAV_MODE);
    };
    badge.addEventListener('click', onClick);
    const offMode = store.subscribe('params.navMode', () => refresh());

    refresh();

    return () => {
        badge.removeEventListener('click', onClick);
        offMode();
        badge.remove();
    };
}
